import type { StateCreator } from 'zustand'
import { apiService } from '@/service/api'
import type {
  IntegrationLinkStatus,
  MeetingIntegrationsStatusResponse,
  ZoomIntegrationStatusDto,
  GoogleMeetIntegrationStatusDto,
  MSTeamsIntegrationStatusDto,
} from '@/types/meeting-integrations'

export interface ZoomIntegrationDetails {
  zoomUserId: string | null
  zoomAccessTokenExpiresAt: string | null
  zoomRefreshTokenExpiresAt: string | null
}

export interface GoogleMeetIntegrationDetails {
  googleUserEmail: string | null
  googleAccessTokenExpiresAt: string | null
}

export interface MSTeamsIntegrationDetails {
  msTeamsUserEmail: string | null
  msTeamsAccessTokenExpiresAt: string | null
}

export interface MeetingIntegrationsState {
  zoomStatus: IntegrationLinkStatus
  zoomIntegrationDetails: ZoomIntegrationDetails | null
  googleMeetStatus: IntegrationLinkStatus
  googleMeetIntegrationDetails: GoogleMeetIntegrationDetails | null
  msTeamsStatus: IntegrationLinkStatus
  msTeamsIntegrationDetails: MSTeamsIntegrationDetails | null
  setZoomIntegration: (payload: ZoomIntegrationStatusDto) => void
  setGoogleMeetIntegration: (payload: GoogleMeetIntegrationStatusDto) => void
  setMSTeamsIntegration: (payload: MSTeamsIntegrationStatusDto) => void
  clearZoomIntegration: () => void
  clearGoogleMeetIntegration: () => void
  clearMSTeamsIntegration: () => void
  fetchMeetingIntegrationsStatus: () => Promise<MeetingIntegrationsStatusResponse | null>
  linkZoomAccount: (
    code: string,
    redirectUri: string
  ) => Promise<ZoomIntegrationStatusDto>
  linkGoogleMeetAccount: (
    code: string,
    redirectUri: string
  ) => Promise<GoogleMeetIntegrationStatusDto>
  linkMSTeamsAccount: (
    code: string,
    redirectUri: string
  ) => Promise<MSTeamsIntegrationStatusDto>
}

export const initialMeetingIntegrationsState: Pick<
  MeetingIntegrationsState,
  'zoomStatus' | 'zoomIntegrationDetails' | 'googleMeetStatus' | 'googleMeetIntegrationDetails' | 'msTeamsStatus' | 'msTeamsIntegrationDetails'
> = {
  zoomStatus: 'unlinked',
  zoomIntegrationDetails: null,
  googleMeetStatus: 'unlinked',
  googleMeetIntegrationDetails: null,
  msTeamsStatus: 'unlinked',
  msTeamsIntegrationDetails: null,
}

const extractZoomDetails = (
  payload: ZoomIntegrationStatusDto
): ZoomIntegrationDetails | null => {
  if (
    !payload.zoomUserId &&
    !payload.zoomAccessTokenExpiresAt &&
    !payload.zoomRefreshTokenExpiresAt
  ) {
    return null
  }

  return {
    zoomUserId: payload.zoomUserId,
    zoomAccessTokenExpiresAt: payload.zoomAccessTokenExpiresAt,
    zoomRefreshTokenExpiresAt: payload.zoomRefreshTokenExpiresAt,
  }
}

const extractGoogleMeetDetails = (
  payload: GoogleMeetIntegrationStatusDto
): GoogleMeetIntegrationDetails | null => {
  if (!payload.googleUserEmail && !payload.googleAccessTokenExpiresAt) {
    return null
  }

  return {
    googleUserEmail: payload.googleUserEmail,
    googleAccessTokenExpiresAt: payload.googleAccessTokenExpiresAt,
  }
}

const extractMSTeamsDetails = (
  payload: MSTeamsIntegrationStatusDto
): MSTeamsIntegrationDetails | null => {
  if (!payload.msTeamsUserEmail && !payload.msTeamsAccessTokenExpiresAt) {
    return null
  }

  return {
    msTeamsUserEmail: payload.msTeamsUserEmail,
    msTeamsAccessTokenExpiresAt: payload.msTeamsAccessTokenExpiresAt,
  }
}

export const meetingIntegrationsSlice: StateCreator<
  MeetingIntegrationsState,
  [['zustand/devtools', never]],
  [],
  MeetingIntegrationsState
> = (set) => ({
  ...initialMeetingIntegrationsState,

  setZoomIntegration: (payload: ZoomIntegrationStatusDto) => {
    set({
      zoomStatus: payload.status,
      zoomIntegrationDetails: extractZoomDetails(payload),
    })
  },

  setGoogleMeetIntegration: (payload: GoogleMeetIntegrationStatusDto) => {
    set({
      googleMeetStatus: payload.status,
      googleMeetIntegrationDetails: extractGoogleMeetDetails(payload),
    })
  },

  setMSTeamsIntegration: (payload: MSTeamsIntegrationStatusDto) => {
    set({
      msTeamsStatus: payload.status,
      msTeamsIntegrationDetails: extractMSTeamsDetails(payload),
    })
  },

  clearZoomIntegration: () => {
    set({
      zoomStatus: 'unlinked',
      zoomIntegrationDetails: null,
    })
  },

  clearGoogleMeetIntegration: () => {
    set({
      googleMeetStatus: 'unlinked',
      googleMeetIntegrationDetails: null,
    })
  },

  clearMSTeamsIntegration: () => {
    set({
      msTeamsStatus: 'unlinked',
      msTeamsIntegrationDetails: null,
    })
  },

  fetchMeetingIntegrationsStatus: async () => {
    try {
      const status = await apiService.getMeetingIntegrationsStatus()
      set({
        zoomStatus: status.zoom.status,
        zoomIntegrationDetails: extractZoomDetails(status.zoom),
        googleMeetStatus: status.googleMeet.status,
        googleMeetIntegrationDetails: extractGoogleMeetDetails(status.googleMeet),
        msTeamsStatus: status.msTeams.status,
        msTeamsIntegrationDetails: extractMSTeamsDetails(status.msTeams),
      })
      return status
    } catch {
      // If the user isn't linked yet, reset to defaults
      set(initialMeetingIntegrationsState)
      return null
    }
  },

  linkZoomAccount: async (code: string, redirectUri: string) => {
    const status = await apiService.linkZoomAccount({ code, redirectUri })
    set({
      zoomStatus: status.status,
      zoomIntegrationDetails: extractZoomDetails(status),
    })
    return status
  },

  linkGoogleMeetAccount: async (code: string, redirectUri: string) => {
    const status = await apiService.linkGoogleMeetAccount({ code, redirectUri })
    set({
      googleMeetStatus: status.status,
      googleMeetIntegrationDetails: extractGoogleMeetDetails(status),
    })
    return status
  },

  linkMSTeamsAccount: async (code: string, redirectUri: string) => {
    const status = await apiService.linkMSTeamsAccount({ code, redirectUri })
    set({
      msTeamsStatus: status.status,
      msTeamsIntegrationDetails: extractMSTeamsDetails(status),
    })
    return status
  },
})

